/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Row } from "@tanstack/react-table";
import { ResourceExecutionStatus } from "~/generated/graphql";
import {
  SUCCESS_STATUSES,
  UNSUCCESS_STATUSES,
  NOT_COMPLETED_STATUSES,
} from "~/views/events/constants/resourceExecutionStatus";
import { ResourceStateCounts } from "~/views/events/hooks/useEventActionResourceCounts";
import { EventEntity } from "../../types";

const INITIAL_COUNTS: ResourceStateCounts = {
  total: 0,
  succeeded: 0,
  failed: 0,
  notCompleted: 0,
  hasData: false,
};

type GetCountsForAction = (eventActionId: string) => ResourceStateCounts;

/**
 * Reducer to aggregate client-side counts from multiple actions
 */
const aggregateClientCounts = (
  acc: ResourceStateCounts,
  counts: ResourceStateCounts
): ResourceStateCounts => ({
  total: acc.total + counts.total,
  succeeded: acc.succeeded + counts.succeeded,
  failed: acc.failed + counts.failed,
  notCompleted: acc.notCompleted + counts.notCompleted,
  hasData: true,
});

/**
 * Reducer to aggregate precomputed counts from multiple actions
 */
const aggregatePrecomputedCounts = (
  acc: ResourceStateCounts,
  action: EventEntity & {
    resourcesSucceeded?: number;
    resourcesFailed?: number;
    resourcesNotCompleted?: number;
  }
): ResourceStateCounts => {
  const succeeded = action.resourcesSucceeded ?? 0;
  const failed = action.resourcesFailed ?? 0;
  const notCompleted = action.resourcesNotCompleted ?? 0;
  const actionTotal = succeeded + failed + notCompleted;

  return {
    total: acc.total + actionTotal,
    succeeded: acc.succeeded + succeeded,
    failed: acc.failed + failed,
    notCompleted: acc.notCompleted + notCompleted,
    hasData: acc.hasData || actionTotal > 0,
  };
};

/**
 * Reducer to count resource states by their status
 */
const countResourceStatesByStatus = (
  acc: ResourceStateCounts,
  state: { status?: string }
): ResourceStateCounts => {
  const status = state.status as ResourceExecutionStatus;
  const isSuccess = SUCCESS_STATUSES.has(status) ? 1 : 0;
  const isUnsuccess = UNSUCCESS_STATUSES.has(status) ? 1 : 0;
  const isNotCompleted = NOT_COMPLETED_STATUSES.has(status) ? 1 : 0;

  return {
    total: acc.total + 1,
    succeeded: acc.succeeded + isSuccess,
    failed: acc.failed + isUnsuccess,
    notCompleted: acc.notCompleted + isNotCompleted,
    hasData: true,
  };
};

/**
 * Gets counts from precomputed fields on the entity
 */
const getPrecomputedCounts = (
  row: Row<EventEntity>
): ResourceStateCounts | null => {
  const hasPrecomputedCounts =
    "resourcesSucceeded" in row.original &&
    "resourcesFailed" in row.original &&
    "resourcesNotCompleted" in row.original;

  if (!hasPrecomputedCounts) {
    return null;
  }

  const entity = row.original as EventEntity & {
    resourcesSucceeded?: number;
    resourcesFailed?: number;
    resourcesNotCompleted?: number;
  };

  const succeeded = entity.resourcesSucceeded ?? 0;
  const failed = entity.resourcesFailed ?? 0;
  const notCompleted = entity.resourcesNotCompleted ?? 0;
  const total = succeeded + failed + notCompleted;

  return {
    total,
    succeeded,
    failed,
    notCompleted,
    hasData: total > 0,
  };
};

/**
 * Gets aggregated counts from batch actions using client-side data
 */
const getBatchClientCounts = (
  actions: Array<EventEntity & { id: string }>,
  getCountsForAction: GetCountsForAction
): ResourceStateCounts | null => {
  const aggregated = actions.reduce<ResourceStateCounts>(
    (acc, action) => {
      if (!action?.id) {
        return acc;
      }

      const counts = getCountsForAction(action.id);
      if (!counts.hasData) {
        return acc;
      }

      return aggregateClientCounts(acc, counts);
    },
    { ...INITIAL_COUNTS }
  );

  return aggregated.hasData ? aggregated : null;
};

/**
 * Gets aggregated counts from batch actions using precomputed data
 */
const getBatchPrecomputedCounts = (
  actions: Array<
    EventEntity & {
      id: string;
      resourcesSucceeded?: number;
      resourcesFailed?: number;
      resourcesNotCompleted?: number;
    }
  >
): ResourceStateCounts => {
  return actions.reduce<ResourceStateCounts>(aggregatePrecomputedCounts, {
    ...INITIAL_COUNTS,
  });
};

/**
 * Gets counts from individual resource states
 */
const getResourceStatesCounts = (
  row: Row<EventEntity>
): ResourceStateCounts => {
  const entity = row.original as EventEntity & {
    resourceStates?: { status?: string }[];
  };
  const states = entity.resourceStates;

  if (!states || states.length === 0) {
    return { ...INITIAL_COUNTS };
  }

  return states.reduce<ResourceStateCounts>(countResourceStatesByStatus, {
    ...INITIAL_COUNTS,
  });
};

/**
 * Gets counts from batch actions (tries client-side first, then precomputed)
 */
const getBatchCounts = (
  row: Row<EventEntity>,
  getCountsForAction: GetCountsForAction
): ResourceStateCounts => {
  const entity = row.original as EventEntity & {
    actions?: Array<EventEntity & { id: string }>;
  };
  const actions = entity.actions ?? [];

  // Try client-side counts first
  const clientCounts = getBatchClientCounts(actions, getCountsForAction);
  if (clientCounts) {
    return clientCounts;
  }

  // Fall back to precomputed counts
  return getBatchPrecomputedCounts(actions);
};

/**
 * Main function to get resource state counts from a table row.
 * Tries multiple strategies in order:
 * 1. Client-side counts (from subscriptions)
 * 2. Precomputed counts (from server)
 * 3. Batch action aggregation
 * 4. Individual resource states
 */
export function getCountsFromResourceStates(
  row: Row<EventEntity>,
  getCountsForAction: GetCountsForAction
): ResourceStateCounts {
  const actionId = (row.original as EventEntity & { id?: string }).id;

  // Try client-side counts first
  if (actionId) {
    const clientCounts = getCountsForAction(actionId);
    if (clientCounts.hasData) {
      return clientCounts;
    }
  }

  // Try precomputed counts
  const precomputedCounts = getPrecomputedCounts(row);
  if (precomputedCounts) {
    return precomputedCounts;
  }

  // Check if this is a batch action
  const isBatch = Array.isArray(
    (row.original as EventEntity & { actions?: unknown[] }).actions
  );

  if (isBatch) {
    return getBatchCounts(row, getCountsForAction);
  }

  // Finally, count from individual resource states
  return getResourceStatesCounts(row);
}
